import React, { useEffect, useState, useCallback, useRef } from "react";
import { cloudinaryThumb, cloudinaryPreview } from "../services/cloudinary";
import { useLocation } from "react-router-dom";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Popup,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import FullscreenControl from "../components/FullscreenControl";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  ChevronDown,
  ChevronUp,
  MapPin,
  Camera,
  User,
  Calendar,
  Hash,
  RotateCcw,
  Undo2,
  Trash2,
  DollarSign,
  CheckCircle,
  Award,
  AlertTriangle,
  Pencil,
  Check,
  X,
} from "lucide-react";
import {
  getCompletedAssignments,
  getAssignmentReadings,
  repeatRoute,
  returnPendingMeters,
  deleteCompletedAssignment,
  marcarPagado,
  updateReading,
  updateAssignmentValor,
} from "../services/api";
import { useAuth } from "../context/AuthContext";

const ROUTE_COLORS = [
  "#D4620D", "#16a34a", "#9333ea", "#dc2626", "#0891b2", "#b45309",
];

function routeColor(routeId) {
  return ROUTE_COLORS[(parseInt(routeId) || 0) % ROUTE_COLORS.length];
}

function MapReadings({ readings }) {
  const valid = readings.filter(
    (r) => !isNaN(parseFloat(r.latitude)) && !isNaN(parseFloat(r.longitude)),
  );
  if (valid.length === 0) return null;

  const avgLat = valid.reduce((s, r) => s + parseFloat(r.latitude), 0) / valid.length;
  const avgLng = valid.reduce((s, r) => s + parseFloat(r.longitude), 0) / valid.length;

  return (
    <MapContainer
      center={[avgLat, avgLng]}
      zoom={15}
      style={{ width: "100%", height: 220, borderRadius: 12 }}
      scrollWheelZoom={false}
    >
      <FullscreenControl />
      <TileLayer
        url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
        attribution="&copy; OpenStreetMap &copy; CARTO"
      />
      {valid.map((r, i) => (
        <CircleMarker
          key={r.id || r.meter_code || i}
          center={[parseFloat(r.latitude), parseFloat(r.longitude)]}
          radius={7}
          pathOptions={{ color: "#D4620D", fillColor: "#D4620D", fillOpacity: 0.85, weight: 2 }}
        >
          <Popup>
            <div style={{ fontFamily: "Montserrat, sans-serif", fontSize: 12 }}>
              {r.meter_code && <strong>{r.meter_code}</strong>}
              {r.reading_value && <div>Lectura: {r.reading_value}</div>}
              <div style={{ color: "#8e8e93" }}>
                {format(new Date(r.created_at), "HH:mm", { locale: es })}
              </div>
            </div>
          </Popup>
        </CircleMarker>
      ))}
    </MapContainer>
  );
}

function AssignmentDetail({ assignmentId }) {
  const [readings, setReadings] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [naturgySaving, setNaturgySaving] = useState({});
  const [zoom, setZoom]           = useState(null); // { url, x, y, ox, oy, level }
  const zoomLevel                 = zoom?.level ?? 1.5;
  const zoomActiveRef             = useRef(false);

  // Listener nativo no-pasivo para bloquear scroll de página mientras hay zoom activo
  useEffect(() => {
    function onWheel(e) {
      if (!zoomActiveRef.current) return;
      e.preventDefault();
      const delta = e.deltaY < 0 ? 0.25 : -0.25;
      setZoom((prev) => prev ? { ...prev, level: Math.min(4, Math.max(1, (prev.level ?? 1.5) + delta)) } : prev);
    }
    window.addEventListener("wheel", onWheel, { passive: false });
    return () => window.removeEventListener("wheel", onWheel);
  }, []);

  useEffect(() => {
    setLoading(true);
    getAssignmentReadings(assignmentId)
      .then(setReadings)
      .catch(() => setReadings([]))
      .finally(() => setLoading(false));
  }, [assignmentId]);

  function startEdit(r) {
    setEditingId(r.route_meter_id);
    setEditValue(r.reading_value ?? "");
  }

  function cancelEdit() {
    setEditingId(null);
    setEditValue("");
  }

  async function saveEdit(r) {
    if (editValue === (r.reading_value ?? "")) { cancelEdit(); return; }
    if (!r.route_meter_id) { cancelEdit(); return; }
    setEditSaving(true);
    try {
      const updated = await updateReading(r.route_meter_id, {
        reading_value: editValue,
        original_value: r.admin_edited ? r.original_value : (r.reading_value ?? null),
      });
      setReadings((prev) =>
        prev.map((x) =>
          x.route_meter_id === r.route_meter_id
            ? { ...x, reading_value: updated.reading_value, admin_edited: true, original_value: updated.original_value }
            : x
        )
      );
      cancelEdit();
    } catch (err) {
      window.alert(err.message || "Error al guardar");
    } finally {
      setEditSaving(false);
    }
  }

  async function toggleNaturgy(r) {
    if (!r.route_meter_id) return;
    const newVal = !r.subido_a_naturgy;
    setNaturgySaving((p) => ({ ...p, [r.route_meter_id]: true }));
    try {
      await updateReading(r.route_meter_id, { subido_a_naturgy: newVal });
      setReadings((prev) =>
        prev.map((x) =>
          x.route_meter_id === r.route_meter_id ? { ...x, subido_a_naturgy: newVal } : x
        )
      );
    } catch (err) {
      window.alert(err.message || "Error al guardar");
    } finally {
      setNaturgySaving((p) => ({ ...p, [r.route_meter_id]: false }));
    }
  }

  if (loading) {
    return <div style={s.detailLoading}>Cargando mediciones...</div>;
  }

  if (!readings || readings.length === 0) {
    return (
      <div style={s.detailEmpty}>
        <Camera size={28} color="#c7c7cc" style={{ marginBottom: 8 }} />
        <p style={{ margin: 0, color: "#8e8e93", fontSize: 13 }}>
          No hay mediciones registradas para esta asignación
        </p>
      </div>
    );
  }

  const incidentes   = readings.filter((r) => r.has_incident).length;
  const naturgyCount = readings.filter((r) => r.subido_a_naturgy).length;

  return (
    <div>
      <div style={{ marginBottom: 14, borderRadius: 12, overflow: "hidden" }}>
        <MapReadings readings={readings} />
      </div>

      <div style={s.detailStats}>
        <span style={s.statChip}>
          <Camera size={12} style={{ marginRight: 5 }} />
          {readings.length} foto{readings.length !== 1 ? "s" : ""}
        </span>
        <span style={s.statChip}>
          <MapPin size={12} style={{ marginRight: 5 }} />
          {new Set(readings.map((r) => `${r.latitude},${r.longitude}`)).size} ubicaciones únicas
        </span>
        {naturgyCount > 0 && (
          <span style={{ ...s.statChip, background: "rgba(22,163,74,0.1)", color: "#16a34a" }}>
            ✓ {naturgyCount} Naturgy
          </span>
        )}
        {incidentes > 0 && (
          <span style={{ ...s.statChip, background: "rgba(245,158,11,0.12)", color: "#b45309" }}>
            <AlertTriangle size={12} style={{ marginRight: 5 }} />
            {incidentes} incidencia{incidentes !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      <div style={s.grid}>
        {readings.map((r) => {
          const isEditing = editingId === r.route_meter_id;
          return (
            <div key={r.reading_id} style={{ ...s.meterCard, ...(r.has_incident ? s.meterCardIncident : {}) }}>
              <div
                style={s.photoWrap}
                onClick={() => !isEditing && r.image_url && setLightbox(cloudinaryPreview(r.image_url))}
                onMouseMove={(e) => {
                  if (!r.image_url) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  const ox = (((e.clientX - rect.left) / rect.width) * 100).toFixed(1);
                  const oy = (((e.clientY - rect.top)  / rect.height) * 100).toFixed(1);
                  zoomActiveRef.current = true;
                  setZoom((prev) => ({ url: r.image_url, x: e.clientX, y: e.clientY, ox, oy, level: prev?.level ?? 1.5 }));
                }}
                onMouseLeave={() => { zoomActiveRef.current = false; setZoom(null); }}
                title="Ver foto completa"
              >
                {r.image_url ? (
                  <img src={cloudinaryThumb(r.image_url, 200, 200)} alt="medidor" style={s.photo} loading="lazy" />
                ) : (
                  <div style={s.photoEmpty}>
                    <Camera size={22} color="#c7c7cc" />
                  </div>
                )}
                {r.has_incident && (
                  <div style={s.incidentBadge}>
                    <AlertTriangle size={10} style={{ marginRight: 3 }} />
                    Incidencia
                  </div>
                )}
                {r.admin_edited && (
                  <div style={s.adminEditedBadge}>
                    <Pencil size={8} style={{ marginRight: 3 }} />
                    Editado Admin
                  </div>
                )}
              </div>

              <div style={s.meterInfo}>
                <div style={s.meterTop}>
                  {r.meter_code && (
                    <div style={s.meterCode}>
                      <Hash size={11} style={{ marginRight: 3, flexShrink: 0 }} />
                      {r.meter_code}
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    {r.checked && <span style={s.checkedBadge}>✓</span>}
                    {r.route_meter_id && !isEditing && (
                      <button
                        style={s.editBtn}
                        title="Editar lectura"
                        onClick={() => startEdit(r)}
                      >
                        <Pencil size={10} />
                      </button>
                    )}
                  </div>
                </div>

                {isEditing ? (
                  <div style={{ marginBottom: 4 }}>
                    <input
                      autoFocus
                      style={s.editInput}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveEdit(r);
                        if (e.key === "Escape") cancelEdit();
                      }}
                    />
                    <div style={s.editActions}>
                      <button style={s.editSaveBtn} onClick={() => saveEdit(r)} disabled={editSaving}>
                        <Check size={10} />
                      </button>
                      <button style={s.editCancelBtn} onClick={cancelEdit} disabled={editSaving}>
                        <X size={10} />
                      </button>
                    </div>
                  </div>
                ) : (
                  r.reading_value != null && r.reading_value !== "" && (
                    <div style={s.readingValue}>{r.reading_value}</div>
                  )
                )}

                {/* Admin editó el valor */}
                {r.admin_edited && (
                  <div style={s.adminEditRow}>
                    <span style={s.adminEditLabel}>✎ {r.admin_edited_by_name || "Admin"}</span>
                    {r.original_value != null && (
                      <span style={s.adminEditOrig}>antes: {r.original_value}</span>
                    )}
                  </div>
                )}

                {/* Subido a Naturgy */}
                <label
                  style={{
                    ...s.naturgyRow,
                    ...(r.subido_a_naturgy ? s.naturgyRowActive : {}),
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!r.subido_a_naturgy}
                    onChange={() => toggleNaturgy(r)}
                    disabled={!r.route_meter_id || naturgySaving[r.route_meter_id]}
                    style={{ width: 13, height: 13, cursor: "pointer", accentColor: "#16a34a", flexShrink: 0 }}
                  />
                  <span style={{ fontSize: 10, fontWeight: 700, color: r.subido_a_naturgy ? "#16a34a" : "var(--text-2)" }}>
                    Naturgy
                  </span>
                  {r.subido_a_naturgy_by_name && (
                    <span style={s.naturgyBy}>{r.subido_a_naturgy_by_name}</span>
                  )}
                </label>

                <div style={s.meterMeta}>
                  <MapPin size={10} style={{ flexShrink: 0, color: "#aeaeb2" }} />
                  <span style={s.coords}>
                    {parseFloat(r.latitude).toFixed(5)}, {parseFloat(r.longitude).toFixed(5)}
                  </span>
                </div>
                <div style={s.meterTime}>
                  {format(new Date(r.created_at), "d MMM · HH:mm", { locale: es })}
                </div>
                {r.reader_name && (
                  <div style={s.meterReader}>
                    <User size={10} style={{ marginRight: 3 }} />
                    {r.reader_name}
                  </div>
                )}
                {r.notes && <div style={s.meterNotes}>{r.notes}</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Lupa hover */}
      {zoom && (() => {
        const W = 320, H = 320;
        const margin = 16;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        let left = zoom.x + 20;
        let top  = zoom.y - H / 2;
        if (left + W > vw - margin) left = zoom.x - W - 20;
        if (top < margin)           top  = margin;
        if (top + H > vh - margin)  top  = vh - H - margin;
        return (
          <div style={{ ...s.zoomPopup, left, top, width: W, height: H, borderColor: zoomLevel >= 3 ? "#f59e0b" : "#D4620D" }}>
            <div style={s.zoomBadge}>{zoomLevel.toFixed(1)}×</div>
            <img
              src={cloudinaryPreview(zoom.url)}
              alt="zoom"
              style={{
                ...s.zoomImg,
                transform: `scale(${zoomLevel})`,
                transformOrigin: `${zoom.ox}% ${zoom.oy}%`,
                transition: "transform 0.1s ease",
              }}
            />
          </div>
        );
      })()}

      {lightbox && (
        <div style={s.lightboxOverlay} onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="medidor" style={s.lightboxImg} />
          <button style={s.lightboxClose} onClick={() => setLightbox(null)}>✕</button>
        </div>
      )}
    </div>
  );
}

const PAGE_SIZE = 20;

export default function RutasCompletadasPage() {
  const { user } = useAuth();
  const isAdminPlus = user?.role === "admin-plus";
  const location = useLocation();
  const pendingOpenId = useRef(location.state?.openAssignmentId || null);

  const [assignments, setAssignments] = useState([]);
  const [total, setTotal]             = useState(0);
  const [page, setPage]               = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const [loading, setLoading]         = useState(true);
  const [expanded, setExpanded]       = useState(null);
  const [repeating, setRepeating]     = useState(null);
  const [returningPending, setReturningPending] = useState(null);
  const [deleting, setDeleting]       = useState(null);
  const [pagando, setPagando]         = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [deletePasswordError, setDeletePasswordError] = useState("");
  const [editingValorId, setEditingValorId] = useState(null);
  const [editingValorValue, setEditingValorValue] = useState("");
  const [savingValor, setSavingValor] = useState(null);

  const loadPage = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await getCompletedAssignments({ page: p, limit: PAGE_SIZE });
      setAssignments(res.data);
      setTotal(res.total);
      setPage(res.page);
      setTotalPages(res.totalPages);
      setExpanded(null);
      if (pendingOpenId.current) {
        const found = res.data.find((a) => a.id === pendingOpenId.current);
        if (found) {
          setExpanded(found.id);
          pendingOpenId.current = null;
          setTimeout(() => {
            document.getElementById(`assignment-${found.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 120);
        }
      }
    } catch {
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPage(1); }, [loadPage]);

  const toggle = useCallback((id) => {
    setExpanded((prev) => (prev === id ? null : id));
  }, []);

  function handleDelete(assignment) {
    setDeleteTarget(assignment);
    setDeletePassword("");
    setDeletePasswordError("");
  }

  async function confirmDelete() {
    if (deletePassword !== "2528") {
      setDeletePasswordError("Contraseña incorrecta");
      return;
    }
    const assignment = deleteTarget;
    setDeleteTarget(null);
    setDeleting(assignment.id);
    try {
      await deleteCompletedAssignment(assignment.id);
      const nextPage = assignments.length === 1 && page > 1 ? page - 1 : page;
      await loadPage(nextPage);
    } catch (err) {
      window.alert(err.message || "Error al eliminar la asignación");
      setDeleting(null);
    }
  }

  async function handleMarcarPagado(assignment) {
    if (!window.confirm(`¿Marcar "${assignment.route_name}" (${assignment.employee_name}) como pagada?`)) return;
    setPagando(assignment.id);
    try {
      await marcarPagado(assignment.id);
      setAssignments(prev =>
        prev.map(a => a.id === assignment.id ? { ...a, pagado: true, pagado_at: new Date().toISOString() } : a)
      );
    } catch (err) {
      window.alert(err.message || "Error al marcar como pagado");
    } finally {
      setPagando(null);
    }
  }

  function startEditValor(a) {
    setEditingValorId(a.id);
    setEditingValorValue(a.valor != null ? String(a.valor) : "");
  }

  function cancelEditValor() {
    setEditingValorId(null);
    setEditingValorValue("");
  }

  async function saveValor(a) {
    const raw = editingValorValue.trim();
    const num = raw === "" ? null : Number(raw.replace(",", "."));
    if (raw !== "" && (isNaN(num) || num < 0)) {
      window.alert("Ingresá un valor numérico válido");
      return;
    }
    setSavingValor(a.id);
    try {
      const updated = await updateAssignmentValor(a.id, num);
      setAssignments((prev) =>
        prev.map((x) => (x.id === a.id ? { ...x, valor: updated.valor } : x))
      );
      cancelEditValor();
    } catch (err) {
      window.alert(err.message || "Error al guardar el precio");
    } finally {
      setSavingValor(null);
    }
  }

  async function handleRepeat(assignment) {
    if (!window.confirm(`¿Crear una nueva copia de la ruta "${assignment.route_name}" con todos los medidores sin marcar?`)) return;
    setRepeating(assignment.id);
    try {
      await repeatRoute(assignment.id);
      window.alert(`Ruta "${assignment.route_name}" creada correctamente. Podés asignarla desde la sección Rutas.`);
    } catch (err) {
      window.alert(err.message || "Error al repetir la ruta");
    } finally {
      setRepeating(null);
    }
  }

  async function handleReturnPending(assignment) {
    if (!window.confirm(`¿Devolverle a ${assignment.employee_name} la ruta "${assignment.route_name}" solo con los medidores sin valor de lectura?`)) return;
    setReturningPending(assignment.id);
    try {
      const res = await returnPendingMeters(assignment.id);
      window.alert(`Se devolvió a ${assignment.employee_name} la ruta "${assignment.route_name}" con ${res.pending_count} medidor${res.pending_count !== 1 ? "es" : ""} sin valor de lectura.`);
    } catch (err) {
      window.alert(err.message || "Error al devolver la ruta");
    } finally {
      setReturningPending(null);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 60, color: "#aeaeb2", textAlign: "center", fontSize: 14 }}>
        Cargando rutas completadas...
      </div>
    );
  }

  return (
    <div>
      <div style={s.header}>
        <h1 style={s.title}>Rutas completadas</h1>
        <p style={s.subtitle}>
          {total} {total === 1 ? "asignación finalizada" : "asignaciones finalizadas"}
        </p>
      </div>

      {assignments.length === 0 ? (
        <div style={s.emptyPage}>
          <MapPin size={36} color="#c7c7cc" style={{ marginBottom: 12 }} />
          <p style={{ color: "#aeaeb2", fontSize: 14, margin: 0 }}>
            Todavía no hay rutas completadas
          </p>
        </div>
      ) : (
        <div style={s.list}>
          {assignments.map((a) => {
            const color = routeColor(a.route_id);
            const isOpen = expanded === a.id;

            return (
              <div key={a.id} id={`assignment-${a.id}`} style={{ ...s.card, borderLeftColor: color }}>
                {/* Card header */}
                <div style={s.cardHeader}>
                  <div
                    style={s.cardLeft}
                    onClick={() => toggle(a.id)}
                  >
                    <span style={{ ...s.dot, background: color }} />

                    <div style={{ minWidth: 0 }}>
                      {/* Title row */}
                      <div style={s.cardTitle}>
                        <span style={s.routeName}>{a.route_name}</span>
                        {a.zona && (
                          <span style={s.pillZona}>Zona {a.zona}</span>
                        )}
                        {!a.route_exists && (
                          <span style={s.pillDeleted}>Ruta eliminada</span>
                        )}
                        {a.pagado && (
                          <span style={s.pillPagado}>
                            <CheckCircle size={10} style={{ marginRight: 3 }} />
                            Pagado
                          </span>
                        )}
                      </div>

                      {/* Meta row */}
                      <div style={s.cardMeta}>
                        <User size={11} style={{ flexShrink: 0 }} />
                        <span>{a.employee_name}</span>
                        <span style={s.metaDot} />
                        <Calendar size={11} style={{ flexShrink: 0 }} />
                        <span>
                          {format(new Date(a.assigned_at), "d MMM yyyy", { locale: es })}
                          {" → "}
                          {a.completed_at
                            ? format(new Date(a.completed_at), "d MMM yyyy", { locale: es })
                            : "—"}
                        </span>
                        <span style={s.metaDot} />
                        <Camera size={11} style={{ flexShrink: 0 }} />
                        <span>
                          <strong>{a.total_lecturas}</strong>{" "}
                          medicion{a.total_lecturas !== "1" ? "es" : ""}
                        </span>
                      </div>

                      {a.notas && (
                        <div style={s.cardNotas}>"{a.notas}"</div>
                      )}

                      {Array.isArray(a.premios_alcanzados) && a.premios_alcanzados.length > 0 && (
                        <div style={s.premiosRow}>
                          {a.premios_alcanzados.map((p) => (
                            <span key={p.id} style={s.premioChip}>
                              <Award size={10} style={{ marginRight: 4, flexShrink: 0 }} />
                              {p.nombre}
                              <span style={s.premioMonto}>
                                ${Number(p.monto).toLocaleString("es-AR")}
                              </span>
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Precio a pagar */}
                      <div style={s.valorRow} onClick={(e) => e.stopPropagation()}>
                        <DollarSign size={12} style={{ color: "#16a34a", flexShrink: 0 }} />
                        {editingValorId === a.id ? (
                          <>
                            <input
                              autoFocus
                              style={s.valorInput}
                              value={editingValorValue}
                              placeholder="0.00"
                              onChange={(e) => setEditingValorValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") saveValor(a);
                                if (e.key === "Escape") cancelEditValor();
                              }}
                            />
                            <button style={s.valorSaveBtn} onClick={() => saveValor(a)} disabled={savingValor === a.id}>
                              <Check size={10} />
                            </button>
                            <button style={s.valorCancelBtn} onClick={cancelEditValor} disabled={savingValor === a.id}>
                              <X size={10} />
                            </button>
                          </>
                        ) : (
                          <>
                            <span style={s.valorText}>
                              {a.valor != null
                                ? `$${Number(a.valor).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : <span style={{ color: "#aeaeb2" }}>Sin precio</span>
                              }
                            </span>
                            {isAdminPlus && (
                              <button style={s.valorEditBtn} onClick={() => startEditValor(a)} title="Editar precio">
                                <Pencil size={10} />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={s.cardActions}>
                    {isAdminPlus && !a.pagado && (
                      <button
                        style={{ ...s.btnPagado, opacity: pagando === a.id ? 0.6 : 1 }}
                        onClick={() => handleMarcarPagado(a)}
                        disabled={pagando === a.id}
                      >
                        <DollarSign size={13} style={{ marginRight: 5 }} />
                        {pagando === a.id ? "Guardando..." : "Marcar pagado"}
                      </button>
                    )}
                    {isAdminPlus && a.route_exists && (
                      <button
                        style={{ ...s.btnRepeat, opacity: repeating === a.id ? 0.6 : 1 }}
                        onClick={() => handleRepeat(a)}
                        disabled={repeating === a.id}
                        title="Crear nueva copia con medidores sin marcar"
                      >
                        <RotateCcw size={13} />
                        {repeating === a.id ? "Creando..." : "Repetir"}
                      </button>
                    )}
                    {isAdminPlus && a.route_exists && (
                      <button
                        style={{ ...s.btnReturnPending, opacity: returningPending === a.id ? 0.6 : 1 }}
                        onClick={() => handleReturnPending(a)}
                        disabled={returningPending === a.id}
                        title="Devolver al operario solo los medidores sin valor de lectura"
                      >
                        <Undo2 size={13} />
                        {returningPending === a.id ? "Devolviendo..." : "Devolver pendientes"}
                      </button>
                    )}
                    {isAdminPlus && (
                      <button
                        style={{ ...s.btnDelete, opacity: deleting === a.id ? 0.6 : 1 }}
                        onClick={() => handleDelete(a)}
                        disabled={deleting === a.id}
                        title="Eliminar del historial"
                      >
                        <Trash2 size={13} />
                        {deleting === a.id ? "..." : "Eliminar"}
                      </button>
                    )}
                    <button style={s.chevronBtn} onClick={() => toggle(a.id)}>
                      {isOpen
                        ? <ChevronUp size={16} color="#8e8e93" />
                        : <ChevronDown size={16} color="#8e8e93" />}
                    </button>
                  </div>
                </div>

                {/* Expanded detail */}
                {isOpen && (
                  <div style={s.cardBody}>
                    <AssignmentDetail assignmentId={a.id} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Password modal for delete */}
      {deleteTarget && (
        <div style={s.pwdOverlay} onClick={(e) => e.target === e.currentTarget && setDeleteTarget(null)}>
          <div style={s.pwdBox}>
            <div style={s.pwdHeader}>
              <Trash2 size={16} color="#dc2626" />
              <span style={s.pwdTitle}>Confirmar eliminación</span>
            </div>
            <p style={s.pwdMsg}>
              Ingresá la contraseña para eliminar <strong>"{deleteTarget.route_name}"</strong> del historial.
            </p>
            <input
              type="password"
              autoFocus
              style={s.pwdInput}
              placeholder="Contraseña"
              value={deletePassword}
              onChange={(e) => { setDeletePassword(e.target.value); setDeletePasswordError(""); }}
              onKeyDown={(e) => e.key === "Enter" && confirmDelete()}
            />
            {deletePasswordError && <p style={s.pwdError}>{deletePasswordError}</p>}
            <div style={s.pwdActions}>
              <button style={s.pwdCancel} onClick={() => setDeleteTarget(null)}>Cancelar</button>
              <button style={s.pwdConfirm} onClick={confirmDelete}>Eliminar</button>
            </div>
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <div style={s.pagination}>
          <button
            style={{ ...s.pageBtn, opacity: page <= 1 ? 0.4 : 1 }}
            onClick={() => loadPage(page - 1)}
            disabled={page <= 1 || loading}
          >
            ← Anterior
          </button>
          <span style={s.pageInfo}>
            {page} / {totalPages}
          </span>
          <button
            style={{ ...s.pageBtn, opacity: page >= totalPages ? 0.4 : 1 }}
            onClick={() => loadPage(page + 1)}
            disabled={page >= totalPages || loading}
          >
            Siguiente →
          </button>
        </div>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s = {
  // Page header
  header:   { marginBottom: 28 },
  title:    { fontSize: 28, fontWeight: 600, color: "var(--text)", margin: "0 0 4px", lineHeight: 1.15 },
  subtitle: { fontSize: 14, color: "#aeaeb2", margin: 0, fontWeight: 400 },

  emptyPage: {
    display: "flex", flexDirection: "column", alignItems: "center", padding: "80px 0",
  },

  // List
  list: { display: "flex", flexDirection: "column", gap: 10 },

  // Card
  card: {
    background: "var(--bg-card)",
    borderRadius: 14,
    borderLeft: "3px solid #e6e6e2",
    border: "1px solid var(--border)",
    borderLeftWidth: 3,
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  },
  cardHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "16px 18px", gap: 12, userSelect: "none",
  },
  cardLeft: {
    display: "flex", alignItems: "flex-start", gap: 12,
    flex: 1, minWidth: 0, cursor: "pointer",
  },
  cardActions: {
    display: "flex", alignItems: "center", gap: 6, flexShrink: 0,
  },

  // Dot
  dot: {
    display: "inline-block", width: 10, height: 10, borderRadius: "50%",
    flexShrink: 0, marginTop: 4,
  },

  // Title row
  cardTitle: {
    display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", marginBottom: 5,
  },
  routeName: { fontSize: 15, fontWeight: 600, color: "var(--text)" },

  // Badges
  pillZona: {
    fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
    background: "rgba(212,98,13,0.09)", color: "#d4620d",
  },
  pillDeleted: {
    fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
    background: "rgba(255,59,48,0.08)", color: "#dc2626",
  },
  pillPagado: {
    display: "inline-flex", alignItems: "center",
    fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
    background: "rgba(52,199,89,0.1)", color: "#16a34a",
  },

  // Meta
  cardMeta: {
    display: "flex", alignItems: "center", gap: 6, fontSize: 12,
    color: "#8e8e93", flexWrap: "wrap",
  },
  metaDot: {
    width: 3, height: 3, borderRadius: "50%", background: "#c7c7cc", flexShrink: 0,
  },
  cardNotas: {
    fontSize: 12, color: "#8e8e93", fontStyle: "italic", marginTop: 5,
  },

  // Precio / valor
  valorRow: {
    display: "flex", alignItems: "center", gap: 6, marginTop: 8,
  },
  valorText: {
    fontSize: 13, fontWeight: 700, color: "#16a34a",
  },
  valorEditBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 18, height: 18, borderRadius: 4, border: "none",
    background: "rgba(22,163,74,0.1)", color: "#16a34a", cursor: "pointer",
    padding: 0, flexShrink: 0,
  },
  valorInput: {
    width: 90, padding: "3px 7px", borderRadius: 6,
    border: "1.5px solid #16a34a", fontSize: 13, fontWeight: 700,
    color: "#16a34a", background: "var(--bg-card)", outline: "none",
    fontFamily: "Montserrat, sans-serif",
  },
  valorSaveBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 22, height: 22, borderRadius: 5, border: "none",
    background: "#16a34a", color: "#fff", cursor: "pointer", padding: 0,
  },
  valorCancelBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 22, height: 22, borderRadius: 5, border: "1px solid var(--border)",
    background: "var(--bg-subtle)", color: "var(--text-2)", cursor: "pointer", padding: 0,
  },

  // Premio chips
  premiosRow: { display: "flex", flexWrap: "wrap", gap: 5, marginTop: 7 },
  premioChip: {
    display: "inline-flex", alignItems: "center",
    background: "rgba(212,98,13,0.07)", border: "1px solid rgba(212,98,13,0.2)",
    color: "#D4620D", borderRadius: 20, padding: "3px 10px",
    fontSize: 11, fontWeight: 600, gap: 2,
  },
  premioMonto: {
    marginLeft: 5, background: "#D4620D", color: "#fff",
    borderRadius: 10, padding: "1px 7px", fontSize: 10, fontWeight: 700,
  },

  // Action buttons
  btnPagado: {
    display: "flex", alignItems: "center", padding: "7px 13px",
    borderRadius: 20, border: "none", cursor: "pointer",
    fontSize: 12, fontWeight: 600, background: "#16a34a", color: "#fff",
    fontFamily: "Montserrat, sans-serif", whiteSpace: "nowrap",
  },
  btnRepeat: {
    display: "flex", alignItems: "center", gap: 5, padding: "7px 12px",
    borderRadius: 20, border: "1px solid var(--border)", cursor: "pointer",
    fontSize: 12, fontWeight: 600, background: "transparent", color: "var(--text-2)",
    fontFamily: "Montserrat, sans-serif", whiteSpace: "nowrap",
  },
  btnReturnPending: {
    display: "flex", alignItems: "center", gap: 5, padding: "7px 12px",
    borderRadius: 20, border: "1px solid rgba(212,98,13,0.3)", cursor: "pointer",
    fontSize: 12, fontWeight: 600, background: "rgba(212,98,13,0.06)", color: "#D4620D",
    fontFamily: "Montserrat, sans-serif", whiteSpace: "nowrap",
  },
  btnDelete: {
    display: "flex", alignItems: "center", gap: 5, padding: "7px 12px",
    borderRadius: 20, border: "1px solid rgba(255,59,48,0.25)", cursor: "pointer",
    fontSize: 12, fontWeight: 600, background: "rgba(255,59,48,0.05)", color: "#dc2626",
    fontFamily: "Montserrat, sans-serif", whiteSpace: "nowrap",
  },
  chevronBtn: {
    background: "rgba(142,142,147,0.1)", border: "none", cursor: "pointer",
    width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
  },

  // Card body (expanded)
  cardBody: {
    borderTop: "1px solid var(--border)", padding: "20px 20px 24px",
  },

  // Detail states
  detailLoading: { textAlign: "center", padding: "32px 0", color: "#aeaeb2", fontSize: 13 },
  detailEmpty:   { display: "flex", flexDirection: "column", alignItems: "center", padding: "32px 0" },

  // Stats chips
  detailStats: { display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" },
  statChip: {
    display: "inline-flex", alignItems: "center",
    background: "var(--bg-subtle)", color: "#8e8e93",
    borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 600,
  },

  // Meter grid
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(155px, 1fr))",
    gap: 12,
  },
  meterCard: {
    background: "var(--bg-subtle)", borderRadius: 12, overflow: "hidden",
    border: "1px solid var(--border)",
  },
  meterCardIncident: {
    border: "2px solid #f59e0b",
    boxShadow: "0 0 0 3px rgba(245,158,11,0.15)",
  },
  photoWrap: {
    position: "relative", width: "100%", paddingBottom: "75%",
    cursor: "pointer", overflow: "hidden", background: "#e6e6e2",
  },
  incidentBadge: {
    position: "absolute",
    bottom: 6,
    left: 6,
    display: "flex",
    alignItems: "center",
    background: "#f59e0b",
    color: "#fff",
    fontSize: 10,
    fontWeight: 700,
    borderRadius: 6,
    padding: "2px 7px",
    zIndex: 1,
  },
  photo: {
    position: "absolute", inset: 0, width: "100%", height: "100%",
    objectFit: "cover",
  },
  photoEmpty: {
    position: "absolute", inset: 0, display: "flex",
    alignItems: "center", justifyContent: "center", background: "var(--bg-subtle)",
  },

  meterInfo:  { padding: "10px 12px 12px" },
  meterTop:   { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 3 },
  meterCode:  { display: "flex", alignItems: "center", fontSize: 12, fontWeight: 600, color: "var(--text)" },
  checkedBadge: {
    fontSize: 10, fontWeight: 700, color: "#16a34a",
    background: "rgba(52,199,89,0.1)", borderRadius: 4, padding: "1px 5px",
  },
  readingValue: { fontSize: 15, fontWeight: 700, color: "#D4620D", marginBottom: 4 },
  meterMeta:  { display: "flex", alignItems: "center", gap: 3, marginBottom: 2 },
  coords:     { fontSize: 10, color: "#aeaeb2", fontFamily: "monospace" },
  meterTime:  { fontSize: 11, color: "#aeaeb2" },
  meterReader:{ display: "flex", alignItems: "center", fontSize: 10, color: "#aeaeb2", marginTop: 2 },
  meterNotes: { fontSize: 11, color: "#aeaeb2", fontStyle: "italic", marginTop: 3 },

  // Inline edit
  editBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 18, height: 18, borderRadius: 4, border: "none",
    background: "rgba(212,98,13,0.1)", color: "#D4620D", cursor: "pointer",
    padding: 0, flexShrink: 0,
  },
  editInput: {
    width: "100%", padding: "4px 7px", borderRadius: 6,
    border: "1.5px solid #D4620D", fontSize: 13, fontWeight: 700,
    color: "#D4620D", background: "var(--bg-card)", outline: "none",
    fontFamily: "Montserrat, sans-serif", boxSizing: "border-box", marginBottom: 4,
  },
  editActions: { display: "flex", gap: 4 },
  editSaveBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 22, height: 22, borderRadius: 5, border: "none",
    background: "#16a34a", color: "#fff", cursor: "pointer", padding: 0,
  },
  editCancelBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 22, height: 22, borderRadius: 5, border: "1px solid var(--border)",
    background: "var(--bg-subtle)", color: "var(--text-2)", cursor: "pointer", padding: 0,
  },
  adminEditedBadge: {
    position: "absolute", top: 6, right: 6,
    display: "flex", alignItems: "center",
    background: "rgba(212,98,13,0.85)", color: "#fff",
    fontSize: 9, fontWeight: 700, borderRadius: 5, padding: "2px 6px", zIndex: 1,
  },
  adminEditRow: {
    display: "flex", alignItems: "center", gap: 4,
    flexWrap: "wrap", marginBottom: 3,
  },
  adminEditLabel: {
    fontSize: 10, fontWeight: 700, color: "#D4620D",
  },
  adminEditOrig: {
    fontSize: 10, color: "#aeaeb2", textDecoration: "line-through",
  },
  naturgyRow: {
    display: "flex", alignItems: "center", gap: 6,
    marginTop: 4, padding: "4px 7px", borderRadius: 6,
    border: "1px solid var(--border)", cursor: "pointer",
    background: "var(--bg-card)", transition: "all .15s",
  },
  naturgyRowActive: {
    borderColor: "#16a34a", background: "rgba(22,163,74,0.06)",
  },
  naturgyBy: {
    fontSize: 10, color: "#aeaeb2",
  },

  // Lupa hover
  zoomPopup: {
    position: "fixed", zIndex: 9998, pointerEvents: "none",
    borderRadius: 14, overflow: "hidden",
    boxShadow: "0 8px 40px rgba(0,0,0,0.45)",
    border: "3px solid #D4620D",
    background: "#000",
  },
  zoomImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  zoomBadge: {
    position: "absolute", top: 8, right: 8, zIndex: 1,
    background: "rgba(0,0,0,0.55)", color: "#fff",
    fontSize: 11, fontWeight: 700, borderRadius: 6, padding: "2px 8px",
    pointerEvents: "none",
  },

  // Lightbox
  lightboxOverlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 9000, padding: 24, cursor: "zoom-out",
  },
  lightboxImg: { maxWidth: "90vw", maxHeight: "90vh", borderRadius: 10, objectFit: "contain" },
  lightboxClose: {
    position: "absolute", top: 20, right: 24,
    background: "rgba(255,255,255,0.15)", border: "none", color: "#fff",
    fontSize: 20, fontWeight: 700, cursor: "pointer", borderRadius: 10,
    width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center",
  },

  // Password delete modal
  pwdOverlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 2000, padding: 24,
  },
  pwdBox: {
    background: "var(--bg-card)", borderRadius: 16, padding: 28,
    width: "100%", maxWidth: 380,
    boxShadow: "0 24px 64px rgba(0,0,0,0.22)", border: "1px solid var(--border)",
  },
  pwdHeader: {
    display: "flex", alignItems: "center", gap: 10, marginBottom: 12,
  },
  pwdTitle: { fontSize: 16, fontWeight: 700, color: "var(--text)" },
  pwdMsg: { fontSize: 13, color: "var(--text-2)", margin: "0 0 16px", lineHeight: 1.5 },
  pwdInput: {
    width: "100%", padding: "10px 13px", borderRadius: 10,
    border: "1.5px solid var(--border)", background: "var(--bg)",
    color: "var(--text)", fontSize: 14, boxSizing: "border-box",
    outline: "none", fontFamily: "Montserrat, sans-serif",
  },
  pwdError: { color: "#dc2626", fontSize: 12, margin: "6px 0 0" },
  pwdActions: {
    display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 18,
    paddingTop: 16, borderTop: "1px solid var(--border)",
  },
  pwdCancel: {
    padding: "9px 18px", borderRadius: 10, border: "1px solid var(--border)",
    background: "var(--bg-subtle)", color: "var(--text-2)", fontWeight: 700,
    cursor: "pointer", fontSize: 13, fontFamily: "Montserrat, sans-serif",
  },
  pwdConfirm: {
    padding: "9px 18px", borderRadius: 10, border: "none",
    background: "#dc2626", color: "#fff", fontWeight: 700,
    cursor: "pointer", fontSize: 13, fontFamily: "Montserrat, sans-serif",
  },

  // Pagination
  pagination: {
    display: "flex", alignItems: "center", justifyContent: "center",
    gap: 16, padding: "28px 0 8px",
  },
  pageBtn: {
    padding: "8px 20px", borderRadius: 20, border: "1px solid var(--border)",
    background: "var(--bg-card)", color: "var(--text-2)",
    fontFamily: "Montserrat, sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer",
  },
  pageInfo: {
    fontSize: 13, color: "#aeaeb2", fontFamily: "Montserrat, sans-serif",
    minWidth: 60, textAlign: "center",
  },
};
